import { Action } from 'redux';

import { TThunkAction } from '../../types/redux';
import { EGetFavoriteEntitiesStatus, IFavoriteEntity } from '../../types/results';
import {
  FAVORITE_ENTITIES_GET_FAVORITE_ENTITIES_TYPE,
  FAVORITE_ENTITIES_FETCH_REMOVE_FAVORITE,
  FAVORITE_ENTITIES_SUCCESS_REMOVE_FAVORITE,
  FAVORITE_ENTITIES_ERROR_REMOVE_FAVORITE,
  COUNTS_SET_COUNTS,
} from '../../constants/actionTypes';
import { notify } from '../notifications';
import { ENotificationStyle } from '../../types/notifications';
import { EEntityType, IFiltersState, EDealType, EOrderField } from '../../types/filters';
import { decrementCount, ISetCounts } from '../counts';
import { trackFavoriteRemove } from './trackings';
import { getCustomUrl, getProduct } from '../../utils/trackings';
import { trackPageview } from '../../utils/trackPageview';
import { fetchUserFavorites } from '../../api/fetchFavoriteEntities';
import { deleteFavorite } from '../../api/deleteFavorite';
import { EEntityTypes, IWebGetUserFavoritesResponse } from '../../repositories/favorites/v1/web-get-user-favorites';
import { IFavoriteCountsSchema } from '../../repositories/favorites/entities/favorite_counts/FavoriteCountsSchema';
import { selectSelectedId } from '../../selectors/folders';

export type TGetFavoriteEntitiesType = 'favoriteEntities/status_changed';

export type TFetchRemoveFavorite = 'favoriteEntities/fetch_remove_favorite';
export type TSuccessRemoveFavorite = 'favoriteEntities/success_remove_favorite';
export type TErrorRemoveFavorite = 'favoriteEntities/error_remove_favorite';

export interface IFetchRemoveFavorite extends Action<TFetchRemoveFavorite> {
  type: TFetchRemoveFavorite;
  entityId: number;
}

export interface ISuccessRemoveFavorite extends Action<TSuccessRemoveFavorite> {
  type: TSuccessRemoveFavorite;
  entityId: number;
}

export interface IErrorRemoveFavorite extends Action<TErrorRemoveFavorite> {
  type: TErrorRemoveFavorite;
}

export interface IGetFavoriteEntitiesSucceed extends Action<TGetFavoriteEntitiesType> {
  type: TGetFavoriteEntitiesType;
  status: EGetFavoriteEntitiesStatus.Succeed;
  filters: IFiltersState;
  favoriteEntities: IFavoriteEntity[];
  hasMore: boolean;
  total: number;
}

export interface IGetFavoriteEntitiesFailed extends Action<TGetFavoriteEntitiesType> {
  type: TGetFavoriteEntitiesType;
  status: EGetFavoriteEntitiesStatus.Failed;
  prevFilters: IFiltersState;
}

export interface IGetFavoriteEntities extends Action<TGetFavoriteEntitiesType> {
  type: TGetFavoriteEntitiesType;
  status: EGetFavoriteEntitiesStatus.Loading | EGetFavoriteEntitiesStatus.NextPageLoading;
}

export type TGetFavoriteEntities = IGetFavoriteEntities | IGetFavoriteEntitiesSucceed | IGetFavoriteEntitiesFailed;
export type TRemoveFavoriteActions = IFetchRemoveFavorite | ISuccessRemoveFavorite | IErrorRemoveFavorite;

export interface IGetFavoriteEntitiesParameters {
  offset: number;
  limit?: number;
  next?: boolean;
  orderField?: EOrderField | null;
}

export interface IRemoveFavoriteParameters {
  entityId: number;
  dealType?: EDealType;
  favoriteEntityType?: EEntityType;
}

export const FAVORITE_ENTITIES_LIMIT = 20;

/* istanbul ignore next */
export function fetchFavoriteEntities(parameters?: IGetFavoriteEntitiesParameters): TThunkAction {
  return (dispatch, getState, context) => {
    const state = getState();
    const offset = parameters && parameters.offset;
    const limit = (parameters && parameters.limit) || FAVORITE_ENTITIES_LIMIT;

    dispatch<TGetFavoriteEntities>({
      status:
        parameters && parameters.next ? EGetFavoriteEntitiesStatus.NextPageLoading : EGetFavoriteEntitiesStatus.Loading,
      type: FAVORITE_ENTITIES_GET_FAVORITE_ENTITIES_TYPE,
    });

    const {
      filters,
      results: { filters: prevFilters },
    } = state;

    let selectedFolderId = null;
    if (!filters.favoriteEntityType || filters.favoriteEntityType !== EEntityType.Newbuilding) {
      selectedFolderId = selectSelectedId(state);
    }

    const residentialEntityTypes = [EEntityType.Commercial, EEntityType.Residential, EEntityType.Suburban];

    const entityTypes = filters.favoriteEntityType
      ? filters.favoriteEntityType === EEntityType.Newbuilding
        ? [EEntityType.Newbuilding]
        : residentialEntityTypes.filter(type => type === filters.favoriteEntityType)
      : residentialEntityTypes;

    return fetchUserFavorites(context, {
      dealType: filters.dealType || undefined,
      entityTypes: (entityTypes as unknown) as EEntityTypes[],
      orderField: filters.orderField || undefined,
      folderId: selectedFolderId,
      offset,
      limit,
    }).then(response => {
      if (response) {
        const mapFetchUserFavoritesResponse = ({
          canLoadMore,
          favoriteEntities,
          counts,
        }: IWebGetUserFavoritesResponse): {
          canLoadMore: boolean;
          favoriteEntities: IFavoriteEntity[];
          counts: IFavoriteCountsSchema;
        } => {
          return {
            canLoadMore,
            favoriteEntities: favoriteEntities.map(({ geo, ...entity }) => ({
              ...entity,
              geo: { ...geo, regionId: geo.regionId ?? 0 },
            })),
            counts,
          };
        };

        const { canLoadMore, favoriteEntities, counts } = mapFetchUserFavoritesResponse(response);

        dispatch<IGetFavoriteEntitiesSucceed>({
          status: EGetFavoriteEntitiesStatus.Succeed,
          total: counts.total,
          filters,
          favoriteEntities: favoriteEntities.map(entity => ({
            ...entity,
            entityId: entity.entityCianId || 0,
          })),
          hasMore: canLoadMore,
          type: FAVORITE_ENTITIES_GET_FAVORITE_ENTITIES_TYPE,
        });

        if (typeof window !== 'undefined') {
          const {
            page,
            user,
            results: { favoriteEntities: favoriteEntitiesAfter, total: totalAfter },
          } = getState();

          trackPageview(page, user, favoriteEntitiesAfter, totalAfter, favoriteEntities.length);
        }

        dispatch<ISetCounts>({
          type: COUNTS_SET_COUNTS,
          data: counts,
        });

        return;
      }

      dispatch<TGetFavoriteEntities>({
        status: EGetFavoriteEntitiesStatus.Failed,
        type: FAVORITE_ENTITIES_GET_FAVORITE_ENTITIES_TYPE,
        prevFilters,
      });

      dispatch(notify('Не удалось получить список избранных объявлений', ENotificationStyle.Error));
    });
  };
}

export function removeFavorite(parameters: IRemoveFavoriteParameters): TThunkAction {
  return (dispatch, getState, context) => {
    dispatch<IFetchRemoveFavorite>({
      type: FAVORITE_ENTITIES_FETCH_REMOVE_FAVORITE,
      entityId: parameters.entityId,
    });

    const { results, user, page } = getState();

    const favoriteEntity = results.favoriteEntities.find(
      favorite => favorite.entityId === parameters.entityId,
    ) as IFavoriteEntity;
    const favoriteEntityType = favoriteEntity.entityType;
    const dealType = favoriteEntity.dealType;

    return deleteFavorite(context, {
      entityType: favoriteEntityType,
      entityId: parameters.entityId,
      dealType,
    })
      .then(response => {
        if (response?.statusCode === 200) {
          /* istanbul ignore next */
          if (favoriteEntity) {
            const customUrl = getCustomUrl(favoriteEntity);
            const product = getProduct(parameters.entityId, results.favoriteEntities);
            trackFavoriteRemove(page, user, customUrl, product, results.total - 1);
          }

          Promise.resolve(
            dispatch<ISuccessRemoveFavorite>({
              type: FAVORITE_ENTITIES_SUCCESS_REMOVE_FAVORITE,
              entityId: parameters.entityId,
            }),
          ).then(() => {
            const {
              results: { favoriteEntities },
            } = getState();

            dispatch(decrementCount(dealType, favoriteEntityType));

            // Декремент счетчика в шапке
            /* istanbul ignore next */
            if (window.__header_updateFavorites__) {
              window.__header_updateFavorites__();
            }

            /**
             * Если пользователь поудалял из избранного много объектов и у него
             * после очередного удаления остается видимым только 1 объект на странице,
             * принудительно запрашиваем следующую страницу выдачи избранного.
             */
            /* istanbul ignore next */
            if (favoriteEntities.length === 1) {
              dispatch(fetchFavoriteEntities({ offset: favoriteEntities.length, next: true }));
            }

            return;
          });

          return;
        }

        dispatch<IErrorRemoveFavorite>({ type: FAVORITE_ENTITIES_ERROR_REMOVE_FAVORITE });
        dispatch(notify('Не удалось удалить из избранного. Попробуйте позже', ENotificationStyle.Error));
      })
      .catch(error => {
        context.logger.error(error);

        dispatch<IErrorRemoveFavorite>({ type: FAVORITE_ENTITIES_ERROR_REMOVE_FAVORITE });
        dispatch(notify('Не удалось удалить из избранного. Попробуйте позже', ENotificationStyle.Error));
      });
  };
}
